import { saveClipFromTranscriptTab } from '@src/services/AIClipProducer';
import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { TranscriptClipFragment } from '@src/services/AIClipProducer';
import type { Result } from '@src/services/common/Result';
import type { IClipProcessorDeps } from '@src/services/IClipProcessorDeps';
import type { Video } from '@src/types.ts/story';

/** The clip on screen. `ClipVideoCreatorAdapter` satisfies it. */
export interface ClipPreviewSaveSource {
  readonly currentVideo: Video | undefined;
  /** Fills `snapshot` from the clip's editor, leaving the live clip alone. */
  prepareCurrentVideoForSave(
    withRenderer: boolean,
    snapshot: Video,
  ): Promise<void>;
}

export interface SaveClipFromPreviewServiceDeps {
  source: ClipPreviewSaveSource;
  /** Scoped to the clip on screen: its transcript, captions and editor. */
  clipProcessorDeps: IClipProcessorDeps;
}

/**
 * PLAT-12001 — Save as Clip from the clip preview's transcript: cut a new clip
 * out of the clip on screen.
 *
 * Never out of the ambient Creator Studio store. In Content Studio that store
 * holds some other video, or one its editor never loaded — and an empty
 * timeline has no video track to load the transcript onto, so the save
 * produced no clip.
 */
export class SaveClipFromPreviewService {
  private readonly _source: ClipPreviewSaveSource;
  private readonly _clipProcessorDeps: IClipProcessorDeps;
  private readonly _logger = LogManager.getLogger(
    'service:SaveClipFromPreviewService',
  );

  constructor(deps: SaveClipFromPreviewServiceDeps) {
    this._source = deps.source;
    this._clipProcessorDeps = deps.clipProcessorDeps;
  }

  async save(
    transcriptPosition: TranscriptClipFragment['transcriptPosition'],
  ): Promise<Result<{ id: string; title: string }>> {
    const live = this._source.currentVideo;
    if (!live) return err('PRECONDITION', 'No clip is open to cut from');

    // A snapshot this save owns, as BackgroundVideoSaveService takes one: the
    // live clip is mutated in place (e.g. TextBrandingService writes its
    // `extraElementData`), so the save must not read it after an await.
    // `prepareCurrentVideoForSave` replaces the editor-owned fields with fresh
    // copies; `extraElementData` is the one it keeps, so it is copied here.
    const snapshot: Video = {
      ...live,
      extraElementData: { ...live.extraElementData },
    };

    try {
      await this._source.prepareCurrentVideoForSave(true, snapshot);
      const saved = await saveClipFromTranscriptTab(
        {
          ...this._clipProcessorDeps,
          // The new clip keeps the clip's own scenes (and their framing), as a
          // save from Creator Studio's transcript does (`skipSceneDetections`).
          // Applying the story's detections would replace them all.
          sceneDetections: undefined,
        },
        snapshot,
        { theme: 'social clip', transcriptPosition },
      );
      if (!saved) {
        return err('UNKNOWN', 'The clip save produced no video');
      }
      return ok(saved);
    } catch (error) {
      this._logger.error('Failed to save the clip', extractErrorInfo(error));
      return err('UNKNOWN', 'Could not save the clip', error);
    }
  }
}
